import React, { useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useRestaurant } from '../../context/RestaurantContext';
import { useT } from '../../context/LanguageContext';
import { QrCode, Download, Copy, Check } from 'lucide-react';
import { downloadQrCard } from '../../lib/qrCard';

/**
 * The public booking link, and its QR.
 *
 * It used to sit on the QR Codes screen, among the table codes and the kiosk
 * code. Those are all doors into ORDERING — a guest scans one at a table or a
 * counter, with food in front of them. This is a door into BOOKING, used
 * before anyone has arrived: it goes on a website, an Instagram bio, a printed
 * card at the desk. Same technology, different job and a different person
 * reaching for it, so it belongs with Reservations.
 *
 * Restaurant-wide by design. A booking is taken for the restaurant and the
 * branch is chosen on the page itself, so unlike a table QR there is nothing
 * per-branch to generate here.
 */

/* Last resort when the printable card cannot be drawn (an image that will not
   load into the canvas): hand over the bare QR rather than nothing. */
function plainDownload(dataUrl, fileName) {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export default function ReservationLink() {
  const { restaurantId, restaurant, location } = useRestaurant();
  const { t } = useT();
  const [qr, setQr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const API_URL = process.env.REACT_APP_API_URL || '/api';
  const restaurantTitle = restaurant?.name || '';

  const generate = async () => {
    if (!restaurantId) return;
    setBusy(true);
    try {
      // `base` is the origin the guest will actually open, so a link generated
      // on a staff laptop still points at the public address.
      const origin = window.location.origin;
      const res = await fetch(
        `${API_URL}/users/restaurant/${restaurantId}/reserve-qr/?base=${encodeURIComponent(origin)}`,
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || t('qr.error.reserve'));
      } else {
        setQr(data.data);
        setError('');
      }
    } catch {
      setError(t('qr.error.reserveNetwork'));
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (!qr?.url) return;
    try {
      await navigator.clipboard.writeText(qr.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — the link is on screen to copy by hand */
    }
  };

  const download = async () => {
    if (!qr?.qr_image) return;
    try {
      // A printable card, not a bare code: the restaurant's name, what the
      // code opens, and a scan-me panel — ready to put on a desk.
      await downloadQrCard({
        qrDataUrl: qr.qr_image,
        title: restaurantTitle,
        subtitle: t('qr.reserve.cardSubtitle'),
        hint: t('qr.card.hint'),
        hint2: t('qr.reserve.cardHint2'),
        footnote: location?.name || '',
        fileName: 'reservation-link-qr.png',
      });
    } catch {
      plainDownload(qr.qr_image, 'reservation-link-qr.png');
    }
  };

  return (
    <div className="p-6 w-full">
      <header className="mb-5">
        <h1 className="font-display text-3xl font-semibold leading-none tracking-tight text-foreground">
          {t('qr.reserve.title')}
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          {t('qr.reserve.pageSub')}
        </p>
      </header>

      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">
          {error}
        </div>
      )}

      <Card className="p-4">
        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="flex-1">
            <Button onClick={generate} disabled={busy || !restaurantId}>
              <QrCode className={`mr-1.5 h-4 w-4 ${busy ? 'animate-pulse' : ''}`} />
              {qr ? t('qr.regenerate') : t('qr.reserve.generate')}
            </Button>

            {qr && (
              <div className="mt-4 space-y-2">
                <div className="break-all rounded bg-gray-50 p-2 text-xs text-muted-foreground">
                  {qr.url}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={copyLink}>
                    {copied ? (
                      <><Check className="mr-1.5 h-3.5 w-3.5" />{t('qr.copied')}</>
                    ) : (
                      <><Copy className="mr-1.5 h-3.5 w-3.5" />{t('qr.copy')}</>
                    )}
                  </Button>
                  <Button variant="outline" size="sm" onClick={download}>
                    <Download className="mr-1.5 h-3.5 w-3.5" />
                    {t('qr.download')}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {qr && (
            <div className="flex items-center justify-center">
              <img
                src={qr.qr_image}
                alt={t('qr.reserve.alt')}
                className="h-44 w-44 rounded border border-gray-100 bg-white object-contain shadow-sm"
              />
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
